import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAttachmentStore } from 'stores/attachmentStore';
import type { SalesOrderAttachmentMeta } from 'types/generatedSchemaTypes';

type ServerAttachment = Pick<SalesOrderAttachmentMeta, 'uid'>;

type UseAttachmentChangesReturn = {
  markChanged: () => void;
  attachmentUidsToSubmit: string[] | undefined;
};

// Set-based: position is not meaningful for attachment uids — same set in a
// different order should not count as a change.
const sameUidSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((uid) => seen.has(uid));
};

const uidsFromServer = (
  serverAttachments?: ReadonlyArray<ServerAttachment | null | undefined> | null
): string[] =>
  (serverAttachments ?? [])
    .map((a) => a?.uid)
    .filter((uid): uid is string => Boolean(uid));

export const useAttachmentChanges = (
  orderUid: string | null | undefined,
  serverAttachments?: ReadonlyArray<ServerAttachment | null | undefined> | null
): UseAttachmentChangesReturn => {
  const allAttachments = useAttachmentStore((state) => state.attachments);

  const attachmentsSignature = useMemo(
    () => uidsFromServer(serverAttachments).join(','),
    [serverAttachments]
  );

  const serverAttachmentsRef = useRef(serverAttachments);
  serverAttachmentsRef.current = serverAttachments;

  const [initialUids, setInitialUids] = useState<string[]>(() =>
    uidsFromServer(serverAttachments)
  );
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (!orderUid) {
      setInitialUids([]);
      setChanged(false);
      return;
    }
    setInitialUids(uidsFromServer(serverAttachmentsRef.current));
    setChanged(false);
  }, [orderUid, attachmentsSignature]);

  const markChanged = useCallback(() => {
    setChanged(true);
  }, []);

  const attachmentUidsToSubmit = useMemo<string[] | undefined>(() => {
    if (!orderUid || !changed) return undefined;
    const currentUids = allAttachments
      .filter((a) => a.entityId === orderUid && !!a.uploadUid)
      .map((a) => a.uploadUid as string);
    if (sameUidSet(currentUids, initialUids)) return undefined;
    return currentUids;
  }, [orderUid, changed, allAttachments, initialUids]);

  return { markChanged, attachmentUidsToSubmit };
};
